import React, { useRef, useState } from "react";
import { IQualification, IQualificationFormModel } from "./types/qualification.types";
import { Button, Form, message, Steps } from "antd";
import HigherEducation from "./utils/higher-education.view";
import SecondaryEducation from "./utils/secondary-education.view";
import Certification from "./utils/certification.view";
import server from "../../../../server";
import { Document }  from "../../../../domains/models/document/index";

const {Step} = Steps;

interface IQualificationSteps {
    title: string;
    fields: string[][];
    content:React.ReactNode;
}

interface IQualificationViewProps {
    loading: boolean;
    viewModel?: IQualificationFormModel;
    onSubmit: (data: IQualificationFormModel) => Promise<void>;
    goToNextTab: () => void;
    qualifications: IQualification[];
    employeeId: string;
}

const QualificationView = (props: IQualificationViewProps) => {
    const [form] = Form.useForm();
    const [current, setCurrent] = useState(0);
    const uploadedDocIds = useRef<string[]>([]);


    function onUploadSuccess(doc: Document) {
        uploadedDocIds.current.push(doc.id);
    }


    const steps: IQualificationSteps[] = [
        {
            title: 'Matriculation',
            fields: [
                ['matriculation', 'passingYear'],
                ['matriculation', 'percentage'],
                ['matriculation', 'boardOrUniversity'],
                ['matriculation', 'documentId']
            ],
            content: <SecondaryEducation
                employeeId={props.employeeId}
                fieldName='matriculation'
                docType={'HSC'}
                onUploadSuccess={onUploadSuccess}/>,
        },
        {
            title: 'Senior Secondary',
            fields: [
                ['seniorSecondary', 'passingYear'],
                ['seniorSecondary', 'percentage'],
                ['seniorSecondary', 'boardOrUniversity'],
                ['seniorSecondary', 'documentId']
            ],
            content: <SecondaryEducation
                employeeId={props.employeeId}
                fieldName='seniorSecondary'
                docType={'SSC'}
                onUploadSuccess={onUploadSuccess}/>,
        },
        {
            title: 'Graduation',
            fields: [['graduation']],
            content:
                <HigherEducation
                    form={form}
                    required={true}
                    docType={'UG'}
                    fieldName='graduation'
                    qualifications={props.qualifications}
                    employeeId={props.employeeId}
                    onUploadSuccess={onUploadSuccess}
                />
        },
        {
            title: 'Post Graduation',
            fields: [['postGraduation']],
            content:
                <HigherEducation
                    form={form}
                    required={false}
                    docType={'PG'}
                    fieldName='postGraduation'
                    qualifications={props.qualifications}
                    employeeId={props.employeeId}
                    onUploadSuccess={onUploadSuccess}
                />
        },
        {
            title: 'Certifications',
            fields: [['certifications']],
            content: <Certification
                employeeId={props.employeeId}
                onUploadSuccess={onUploadSuccess}/>,
        },
    ];

    function resetCurrentStep() {
        const currentFields = steps[current].fields;
        form.resetFields(currentFields);
        message.info(`Step ${current + 1} fields reset`);
    }

    async function handleFormFinish() {
        let data: IQualificationFormModel;
        try {
            console.log('Qualification Detail Form Values:', form.getFieldsValue());
            data = await form.validateFields();

        } catch (errorInfo: any) {
            const firstError = errorInfo.errorFields?.[0];
            if (firstError) {
                const firstErrorPath = firstError.name[0];

                // find which step contains this error
                const errorStepIndex = steps.findIndex((step: IQualificationSteps) =>
                    step.fields.some((field) => field[0] === firstErrorPath)
                );

                if (errorStepIndex >= 0) {
                    setCurrent(errorStepIndex);
                }

                // scroll to the field smoothly
                setTimeout(() => {
                    form.scrollToField(firstError.name, {block: 'center'});
                }, 200);
            }
            message.error("Error in form fields!");
            return;
        }
        try {
            await props.onSubmit(data);
        } catch (error: any) {
            await server.document.deleteMultiple(uploadedDocIds.current);
        }
    }

    return (
        <div className={"pn__employee-create__tab qualification-detail"}>
            <Steps
                size="small"
                current={current}
                onChange={setCurrent}
                style={{maxWidth: '1024px', margin: '16px auto 24px'}}
                className={"pn__employee-create__steps"}
            >
                {steps.map((step, i) => (
                    <Step
                        key={i}
                        title={step.title}
                        className={"pn__employee-create__step"}
                    />
                ))}
            </Steps>

            <Form
                form={form}
                layout={'vertical'}
                className={"pn__employee-create__form qualification-detail"}
                initialValues={props.viewModel}
            >
                {
                    steps.map((step: IQualificationSteps, i: number) => (
                        <div
                            key={i}
                            className={"pn__employee-create__step-section"}
                            style={{display: i === current ? 'block' : 'none'}}
                        >
                            {step.content}
                        </div>
                    ))
                }
            </Form>

            <div className={'pn__employee-create__form-actions'}>
                <Button
                    danger
                    size={'large'}
                    disabled={props.loading}
                    onClick={resetCurrentStep}
                    className={'reset-button'}
                >
                    Reset Current Step
                </Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    size={'large'}
                    disabled={props.loading}
                    onClick={handleFormFinish}
                    className={'submit-button'}
                >
                    Submit Form
                </Button>
            </div>
        </div>
    )
}

export default QualificationView;
